<template>
    <div class="o-d-guide-path-panel d-guide-path-panel">
        <!--- Print blocks -->
        <div class="d-guide-print__header">
            <div class="d-guide-print__logo-wrapper">
                <svg-icon
                    original
                    name="icons/logo_mobile"
                    class="d-guide-print__logo"
                />
            </div>
            <div class="d-guide-print__container">
                <MExtremePoint
                    :label="$t('ui.start')"
                    :value="t(startPoint, 'name')"
                    :pointIcon="false"
                    :inputElement="false"
                />
                <MExtremePoint
                    :label="$t('ui.finish')"
                    :value="t(finishPoint, 'name')"
                    :pointIcon="false"
                    :inputElement="false"
                    theme="finish"
                />
                <div class="d-guide-print__summary">
                    <span>
                        {{ $t('ui.total-distance') }}
                    </span>
                    <span>
                        {{ distance }} {{ $t('ui.meter') }}
                    </span>
                </div>
            </div>
            <OWQrGenerator
                v-if="isExistFinishPoint"
            />
        </div>
        <div class="d-guide-print__footer">
            <img
                class="d-guide-print__footer-image"
                src="../../../assets/images/mvg-logo.png"
            >
        </div>
        <!-- General component -->
        <div class="d-guide-path-panel__header">
            <div class="d-guide-path-panel__header-wrapper">
                <h3 class="d-guide-path-panel__header-title">
                    {{ $t('o-d-guide-path-panel_header-title') }}
                </h3>
            </div>
            <AButton
                theme="dark"
                :text="$t('ui.close')"
                @onClick="redirectToHome"
            >
                <template #left>
                    <svg-icon
                        original
                        name="icons/close"
                        class="d-guide-path-panel__header-close-icon"
                    />
                </template>
            </AButton>
        </div>
        <div class="d-guide-path-panel__extreme-points">
            <MExtremePoint
                :label="$t('ui.start')"
                :value="t(startPoint, 'name')"
                :editIcon="true"
                :pointIcon="false"
                :inputElement="false"
                isComponentClickable
                @onActivate="handleInputFocus('start')"
            />
            <MExtremePoint
                :label="$t('ui.finish')"
                :value="t(finishPoint, 'name')"
                :placeholder="$t('d-guide-search-placeholder--finish')"
                :editIcon="true"
                :pointIcon="false"
                :inputElement="false"
                theme="finish"
                isComponentClickable
                @onActivate="handleInputFocus('finish')"
            />
        </div>
        <div
            v-if="isExistFinishPoint"
            class="d-guide-path-panel__overview"
        >
            <AGuideOverviewButton
                :active="isActiveOverview"
                @onClick="handleClickOverview"
            />
        </div>
        <div
            v-if="isExistFinishPoint"
            class="d-guide-path-panel__steps"
        >
            <ODPathSteps />
        </div>
        <div class="d-guide-path-panel__wrapper">
            <div class="d-guide-path-panel__bottom-block">
                <div class="d-guide-path-panel__bottom-block--buttons">
                    <AButton
                        class="d-guide-path-panel__send-sms-button"
                        theme="dark"
                        :text="$t('ui.send-sms')"
                        @onClick="handleSendSms"
                    >
                        <template #right>
                            <svg-icon
                                original
                                name="icons/send"
                                class="d-guide-path-panel__bubble-icon"
                            />
                        </template>
                    </AButton>
                    <AMBackButton
                        class="d-guide-path-panel__back-button"
                        theme="back"
                        :text="$t('ui.back')"
                        @onClick="handleClickBack"
                    />
                </div>
                <OWQrGenerator
                    v-if="isExistFinishPoint"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { 
    mapState,
    mapGetters
} from 'vuex';
    
import AButton from '../../atoms/a-button/a-button';
import AMBackButton from '../../atoms/a-m-back-button/a-m-back-button';
import MExtremePoint from '../../molecules/m-extreme-point/m-extreme-point';
import OWQrGenerator from '../../organisms/o-w-qr-generator/o-w-qr-generator';
import ODPathSteps from '../../unique-organisms/o-d-path-steps/o-d-path-steps';
import AGuideOverviewButton from '../../atoms/a-guide-overview-button/a-guide-overview-button';

import { WEB_START } from '../../../utils/constants/pages';
import { SHOW_MODAL } from "../../../utils/constants/bus-events-constants";
import { REMOVE_PATH } from "../../../shared/components/map-viewer/constants/map-commands";
import { START_GUIDE_OVERVIEW } from '../../../utils/constants/bus-events-constants';

import firstElementTabNavigation from '../../../utils/mixins/first-element-tab-navigations';

export default {
    name: 'ODGuidePathPanel',
    components: {
        AButton,
        ODPathSteps,
        AMBackButton,
        MExtremePoint,
        OWQrGenerator,
        AGuideOverviewButton
    },
    props: {
        startPoint: {
            type: Object,
            required: true
        },
        finishPoint: {
            type: Object,
            required: true
        }
    },
    mixins: [firstElementTabNavigation],
    data() {
        return {
            tabNavigationStartElement: '.d-guide-path-panel__steps .d-path-steps__button'
        }
    },
    computed: {
        ...mapState({
            activeStep: state => state.activeStep,
            activeStepIndex: state => state.activeStepIndex,
            activeLocale: state => state.activeLocale,
            distance: state => state.activePath?.distance
        }),
        ...mapGetters([
            't'
        ]),
        isExistFinishPoint() {
            return !_.isEmpty(this.finishPoint);
        },
        isActiveOverview() {
            return this.activeStepIndex === null;
        }
    },
    watch: {
        activeLocale() {
            this.alignLabelsExtremePoints();
        }
    },
    mounted() {
        this.alignLabelsExtremePoints();
    },
    methods: {
        handleClickBack() {
            const params = {
                name: `web.${this.$route.params.type}`,
                params: {
                    id: this.$route.params.id
                }
            };
            this.$router.push(params);
        },
        handleSendSms() {
            const params = {
                component: 'sms',
                data: {}
            };
            this.$bus.$emit(SHOW_MODAL, params);
        },
        handleInputFocus(data) {
            this.$emit('onChangePoint', data);
        },
        alignLabelsExtremePoints() {
            const arr = [...document.getElementsByClassName('extreme-point__point')];
            arr.forEach(item => {
                item.style.width = 'auto';
            });
            const values = [];
            
            this.$nextTick(() => {
                arr.forEach(item => {
                    values.push(parseInt(item.offsetWidth)+ 0.5);
                });
                arr.forEach(item => {
                    item.style.width = Math.max(...values) + 'px';
                });
            })

            // if (!this.$route.query.to) {
            //     const el = [...document.getElementsByClassName('extreme-point--finish')];
            //     el[0].style.border = '1px solid #1377EF';
            // }
        },
        redirectToHome() {
            this.$bus.$emit(REMOVE_PATH);
            const params = {
                name: WEB_START
            };
            this.$router.push(params);
        },
        handleClickOverview() {
            if (this.activeStep === null) return;
            this.$bus.$emit(START_GUIDE_OVERVIEW, true);
            this.$store.commit('setActiveStep', null);
            this.$store.commit('setActiveStepIndex', null);
        }
    }
}
</script>

<style lang="scss">
@import "o-d-guide-path-panel";   
</style>
